import { Box, Grid } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { END_POINTS } from "constants/APIConstant";
import fetch from "auth/FetchInterceptor";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import CircularProgress from "@mui/material/CircularProgress";
import SnackbarModal from "component/shared/snackbar";
import ReactGA from "react-ga4";
import { useDispatch, useSelector } from "react-redux";
import { apiResponseMessage } from "redux/actions/ApiMessageResponse";
import { MESSAGE_TYPE } from "redux/constants/ApiMessageResponse";

export default function ResetPass() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dir = useSelector(({ theme }) => theme.direction);

  const { id } = useParams();

  const { token } = useSelector(({ auth }) => {
    return auth;
  });

  ReactGA.initialize(END_POINTS.TRACKING_ID);

  useEffect(() => {
    // Send pageview with a custom path
    ReactGA.send({
      hitType: "pageview",
      page: `/resetPass/${id}`,
      title: `resetPass page`,
    });
  }, [id]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => {
      clearTimeout(timeOut);
    };
  }, [id]);

  const [loadingBtn, setLoadingBtn] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // feedback
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // error
  const [openError, setOpenError] = useState(false);
  const handleOpenError = () => {
    setOpenError(true);
  };
  const handleCloseError = () => {
    setOpenError(false);
  };

  const [resetPass, setResetPass] = useState({
    token,
    password: "",
    oldPassword: "",
    confirmPassword: "",
  });

  const handleResetPass = (e) => {
    let myPass = { ...resetPass };
    myPass[e.target.name] = e.target.value;
    setResetPass(myPass);
  };

  async function resetPassForm(e) {
    e.preventDefault();
    setLoadingBtn(true);

    let passValidate = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W,_]).{8,32}$/;

    if (
      resetPass.password.match(passValidate) &&
      resetPass.confirmPassword.match(passValidate) &&
      resetPass.password === resetPass.confirmPassword
    ) {
      await fetch
        .post(`${END_POINTS.RESET_PASS}`, resetPass)
        .then((res) => {
          if (res.state === true) {
            dispatch(
              apiResponseMessage({
                showMessage: true,
                message: <FormattedMessage id="sent-successfully" />,
                messageType: MESSAGE_TYPE.SUCCESS,
              })
            );
            setLoadingBtn(false);
            setTimeout(() => {
              handleClose();
              navigate(`${APP_PREFIX_PATH}/login`);
            }, 2000);
          } else {
            dispatch(
              apiResponseMessage({
                showMessage: true,
                message: res?.message || (
                  <FormattedMessage id="error-ratelimit" />
                ),
                messageType: MESSAGE_TYPE.ERROR,
              })
            );
            setLoadingBtn(false);
            setTimeout(() => {
              handleCloseError();
            }, 10000);
          }
        })
        .catch((e) => {
          dispatch(
            apiResponseMessage({
              showMessage: true,
              message: e.response?.data?.message || e.response?.data?.error || (
                <FormattedMessage id="error-ratelimit" />
              ),
              messageType: MESSAGE_TYPE.ERROR,
            })
          );
          setLoadingBtn(false);
          setTimeout(() => {
            handleCloseError();
          }, 10000);
        });
    } else {
      setLoadingBtn(false);
      if (
        !resetPass.password.match(passValidate) ||
        !resetPass.confirmPassword.match(passValidate)
      ) {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: <FormattedMessage id="password-validation" />,
            messageType: MESSAGE_TYPE.ERROR,
          })
        );
        setTimeout(() => {
          handleCloseError();
        }, 10000);
      } else if (resetPass.password !== resetPass.confirmPassword) {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: <FormattedMessage id="passwordMatch" />,
            messageType: MESSAGE_TYPE.ERROR,
          })
        );
        setTimeout(() => {
          handleCloseError();
        }, 10000);
      }
    }
  }

  return (
    <div className="d-flex align-items-center flex-column py-5">
      <Grid item xs={12} sx={{ textAlign: "center", mb: 3 }}>
        <h2 style={{ color: "var(--main-color)" }}>
          <FormattedMessage id="resetPass" />
        </h2>
      </Grid>
      <Box
        sx={{
          border: "1px solid #E8E8E8",
          borderRadius: "0.75rem",
          width: "50%",
          minWidth: "300px",
        }}
        className={"shadow"}
      >
        <div className="p-5">
          <form onSubmit={resetPassForm}>
            <div className="mb-3">
              <label className="mb-2 fw-bold" htmlFor={"oldPassword"}>
                {intl.formatMessage({ id: "oldPassword" })}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showOldPassword ? "text" : "password"}
                  onChange={handleResetPass}
                  className="bg-transparent form-control fw-bold mx-auto py-2"
                  style={{ fontSize: "14px" }}
                  required
                  name={"oldPassword"}
                  id={"oldPassword"}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  style={{
                    position: "absolute",
                    right: dir === "rtl" ? "auto" : "20px",
                    left: dir === "rtl" ? "20px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  {showOldPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="mb-2 fw-bold" htmlFor={"password"}>
                {intl.formatMessage({ id: "password" })}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  onChange={handleResetPass}
                  className="bg-transparent form-control fw-bold mx-auto py-2"
                  style={{ fontSize: "14px" }}
                  required
                  name={"password"}
                  id={"password"}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: dir === "rtl" ? "auto" : "20px",
                    left: dir === "rtl" ? "20px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
            <div className="mb-4">
              <label className="mb-2 fw-bold" htmlFor={"confirmPassword"}>
                {intl.formatMessage({ id: "reenter-password" })}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  onChange={handleResetPass}
                  className="bg-transparent form-control fw-bold mx-auto py-2"
                  style={{ fontSize: "14px" }}
                  required
                  name={"confirmPassword"}
                  id={"confirmPassword"}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: dir === "rtl" ? "auto" : "20px",
                    left: dir === "rtl" ? "20px" : "auto",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
            <div className="text-center">
              <button type="submit" className="main-btn mx-auto">
                <span>
                  {loadingBtn ? (
                    <CircularProgress sx={{ color: "#fff" }} size={26} />
                  ) : (
                    intl.formatMessage({ id: "confirm" })
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>
      </Box>

      {/* feedback */}
      <SnackbarModal open={open} onClose={handleClose} severity="success" />

      {/* error */}
      <SnackbarModal
        open={openError}
        onClose={handleCloseError}
        severity="error"
      />
    </div>
  );
}
